// A non-state party (Hezbollah, the Houthis, the PMF) on the diplomacy screen. It has no country
// report, so what is known of it comes from the snapshot's relation to the leader's state and
// from the wars it fights in:
//
//   snapshot.relations[index] ─► relation + reason + sources
//   snapshot.activeWars       ─► wars listing it as a side ─► warView.statements by this party

import type { Statement, War } from '../core/schema.ts';
import type { WorldService } from '../services/api.ts';
import type { RelationInfo, WorldSnapshot } from '../world/api.ts';
import { warViewOf } from './extensions.ts';

export interface Party {
  id: string;
  name: string;
  relation: RelationInfo;
  /** Wars live at the date that list it as a side. */
  wars: War[];
  /** Its own statements in those wars, newest first. */
  statements: Statement[];
}

/** Snapshot actor index of an actor id, or null when the snapshot holds no relation for it. */
export function indexOfActor(world: WorldService, snap: WorldSnapshot, id: string): number | null {
  for (const index of snap.relations.keys()) {
    if (world.actor(index)?.id === id) {
      return index;
    }
  }
  return null;
}

/** The relation of an actor to the leader's state at the snapshot's date, or null. */
export function relationOf(world: WorldService, snap: WorldSnapshot | null, id: string): RelationInfo | null {
  if (!snap) {
    return null;
  }

  const index = indexOfActor(world, snap, id);
  return index === null ? null : (snap.relations.get(index) ?? null);
}

function isSide(war: War, id: string): boolean {
  return war.sides.a.includes(id) || war.sides.b.includes(id);
}

/** The party at the snapshot's date; null when the snapshot has no relation for it. */
export function partyOf(world: WorldService, snap: WorldSnapshot | null, id: string): Party | null {
  const index = snap ? indexOfActor(world, snap, id) : null;
  const relation = relationOf(world, snap, id);
  if (!snap || index === null || !relation) {
    return null;
  }

  const wars = snap.activeWars.filter((w) => isSide(w, id));
  const statements = wars
    .flatMap((w) => warViewOf(world, w.id)?.statements ?? [])
    .filter((s) => s.actor === id)
    .sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0));

  return { id, name: world.actor(index)?.name ?? id, relation, wars, statements };
}
